import {Component, ContentChildren, EventEmitter, Input, OnInit, Output, QueryList, ViewChild} from '@angular/core';
import {NzModalService} from 'ng-zorro-antd/modal';
import {NzTableComponent} from 'ng-zorro-antd/table';
import {GnTableColumnDirective} from './table-column.directive';
import {HttpClient} from '@angular/common/http';
import {GnObjectUtils, pipeTypes} from 'geminis-ng-core';
import {GnMessageService} from '../message-service/message-service';
import {DataStrategy} from './DataStrategy';
import {RemoteDataStrategy} from './RemoteDataStrategy';
import {LocalDataStrategy} from './LocalDataStrategy';

@Component({
  selector: 'gn-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css']
})
export class GnTableComponent implements OnInit {

  private dataStrategies: { [key: string]: DataStrategy } = {};

  @ViewChild(NzTableComponent, {static: true}) nzTable: NzTableComponent;

  @Input() url: string;
  @Input() includes: string[] = [];
  @Input() filterGroup: any;
  @Input() sorts: any[] = [];

  @Input() data: any[] = [];
  @Input() dataKey: string;
  @Input() dataTotal = 0;

  @Input() pageIndex = 1;
  @Input() pageSize = 10;
  @Input() showPagination = true;
  @Input() showSizeChanger = true;

  @Input() autoLoadData = true;

  @Input() selectionMode: 'none' | 'single' | 'multi' = 'multi';

  @Input() dataStrategy: 'remote' | 'local' = 'remote';


  @Output() dataSaved = new EventEmitter();
  @Output() dataDeleted = new EventEmitter();
  @Output() dataLoaded = new EventEmitter();

  selectionStatus: { [key: string]: boolean } = {};
  selected: any[] = [];
  selectedKeys: any[] = [];
  selectedKey: any;
  selectedOne: any;
  isSelected = false;
  isAllSelected = false;
  isSomeSelected = false;
  isSingleSelected = false;
  editData: any = null;

  isLoading = false;

  @ContentChildren(GnTableColumnDirective) columns: QueryList<GnTableColumnDirective>;

  constructor(public http: HttpClient,
              public modalService: NzModalService,
              public messageService: GnMessageService) {
    this.dataStrategies.remote = new RemoteDataStrategy(this);
    this.dataStrategies.local = new LocalDataStrategy(this);
  }

  getValue(row: any, key: string): any {
    return GnObjectUtils.getValue(row, key);
  }

  getValueWithPipe(row: any, key: string, pipe: string, pipeArgs: any[]): any {
    if (!pipe) {
      return this.getValue(row, key);
    }

    const pipeTransform = pipeTypes[pipe];
    if (!pipeTransform) {
      throw new Error('管道[' + pipe + ']未注册');
    }

    const value = this.getValue(row, key);
    return pipeTransform.transform(value, pipeArgs);
  }

  loadData(): Promise<any> {
    this.isLoading = true;
    return this.dataStrategies[this.dataStrategy].load()
      .then((resp: any) => {
        this.data = resp?.content;
        this.dataTotal = resp?.totalElements;
        this.dataLoaded.emit(this.data);
        return this.data;
      })
      .catch(error => {
        this.messageService.error('获取数据错误');
        throw error;
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  loadDataAndCleanSelection(): void {
    this.changeAllSelectStatus(false);
    this.loadData();
  }

  deleteData(url: string, id: any[]): void {
    this.modalService.confirm({
      nzContent: '确定删除所选数据？',
      nzOnOk: () => {
        this.isLoading = true;
        return this.dataStrategies[this.dataStrategy].delete(url, id)
          .then(() => {
            this.messageService.success('删除数据成功');
            this.dataDeleted.emit();
          })
          .catch(err => {
            this.messageService.error('删除数据错误');
            console.error(err);
          })
          .finally(() => {
            this.isLoading = false;
            this.loadDataAndCleanSelection();
          });
      }
    }, 'confirm');
  }

  deleteSelectedData(): void {
    this.deleteData(this.url, this.selectedKeys);
  }

  refreshSelectionStatus(): void {
    this.selected = this.data.filter(item => this.selectionStatus[this.getValue(item, this.dataKey)]);
    this.selectedKeys = this.selected.map(item => this.getValue(item, this.dataKey));
    this.isSelected = this.selectedKeys.length > 0;
    this.isAllSelected = this.isSelected && this.selectedKeys.length === this.data.length;
    this.isSomeSelected = this.isSelected && this.selectedKeys.length < this.data.length;
    this.isSingleSelected = this.selectedKeys.length === 1;
    this.selectedKey = this.isSelected ? this.selectedKeys[0] : null;
    this.selectedOne = this.isSelected ? this.selected[0] : null;
  }

  changeSelectStatus(key: any): void {
    if (this.selectionMode === 'none') {
      return;
    }
    if (this.selectionMode === 'single') {
      this.selectionStatus = {};
    }
    this.selectionStatus[key] = !this.selectionStatus[key];
    this.refreshSelectionStatus();
  }

  changeAllSelectStatus(selected: boolean): void {
    this.data.forEach(item => this.selectionStatus[this.getValue(item, this.dataKey)] = selected);
    this.refreshSelectionStatus();
  }

  selectOne(dataKey: any): void {
    this.select([dataKey]);
  }

  select(dataKeys: any[]): void {
    this.changeAllSelectStatus(false);
    dataKeys.forEach(dataKey => this.selectionStatus[dataKey] = true);
    this.refreshSelectionStatus();
  }

  ngOnInit(): void {
    if (this.autoLoadData) {
      this.loadData();
    }
  }

  refreshData(data: any[]): void {
    this.data = data;
    this.dataTotal = data.length;
  }

  startEdit(data: any): void {
    this.editData = data;
  }

  endEdit(): void {
    this.isLoading = true;
    this.dataStrategies[this.dataStrategy].save(this.url, this.editData)
      .then((resp: any) => {
        this.dataSaved.emit(this.editData);
        this.editData = null;
      })
      .catch(error => {
        this.messageService.error('更新数据错误');
        throw error;
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}
